import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import moment from 'moment';
import { toastr } from 'react-redux-toastr';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import { tableAdjustableColOptions, renderCustomHeader } from '../../component/helpers/table';
import { formatTimestamp } from '../../component/helpers/tablesFuncHelpers';
import momenttz from '../../component/helpers/momenttz';
import ModalProjectVersion from './ModalProjectVersion';
import GreyHeader from '../../component/GreyHeader';
import CustomButton from '../../component/CustomButton';
import CalendarDropdown from "../../component/smart/CalendarDropdown";
import BaseTimePicker from "../../component/smart/BaseTimePicker";
import { addRelease } from "../../action/FleetAdminActions";
import { getReleases } from "../../action/ClientActions";

const ReleasesPage = (props) => {
	const { t, i18n } = useTranslation('translation', { keyPrefix: 'releasesPage.releasesPage' });
	const dispatch = useDispatch();

	const [releases, setReleases] = useState([]);
	const [timestamp, setTimestamp] = useState(moment(moment().format('YYYYMMDD0900'), 'YYYYMMDDHHmm'));
	const [wikilink, setWikilink] = useState('');
	const [errMessage, setErrMessage] = useState({
		errtimestamp: '',
		errwikilink: '',
	});
	const { errwikilink } = errMessage;
	const [showProjectVersion, setShowProjectVersion] = useState(false);

	useEffect(() => {
		getReleasesList();
	}, []);

	const toggleLoader = useCallback((show) => {
		dispatch({ type: 'TOGGLE_LOADER', payload: show });
	}, [dispatch]);

	const getReleasesList = () => {
		toggleLoader(true);

		getReleases()
			.then(res => {
				toggleLoader(false);
				let { releases } = res.data.response;
				releases = _.orderBy(releases, ['timestamp'], ['desc']);
				setReleases(releases);
			})
			.catch(err => {
				toggleLoader(false);
				toastr.error(t('unableGetReleases'));
			})
	}

	const onDescriptionChange = (e) => {
		setWikilink(e.target.value);
	}

	const onDateChange = (current) => {
		setTimestamp(moment(current).set({
			hour: moment(timestamp).hour(),
			minute: moment(timestamp).minute()
		}));
	};

	const onTimeChange = (current) => {
		setTimestamp(moment(moment(current).format('YYYYMMDDHHmm'), 'YYYYMMDDHHmm'));
	}

	const checkData = (data) => {
		const updatedErrMessage = { ...errMessage };
		let error = false;

		Object.keys(data).forEach((key) => {
			if (!data[key]) {
				error = true;
				updatedErrMessage[`err${key}`] = `${t('textEnterYour')} ${key}!`;
			} else if (updatedErrMessage[key]) {
				updatedErrMessage[`err${key}`] = null;
			}
		})

		setErrMessage(updatedErrMessage);
		return error;
	}

	const onAddRelease = (event) => {
		event.preventDefault();

		const data = {
			timestamp: momenttz(timestamp.format('YYYY-MM-DD HH:mm:ss')).utc().format('YYYYMMDDHHmmss'),
			wikilink,
		};
		const error = checkData(data);

		if (!error) {
			toggleLoader(true);
			addRelease(data)
				.then((res, err) => {
					getReleasesList();
					toastr.success('Success', t('releaseAdded'));
				})
				.catch((error) => {
					toggleLoader(false);
					let errDescription = i18n.t('errorMessage.errDescription');
					if (error.response.data.response.error) {
						errDescription = error.response.data.response.error;
					}
					toastr.error(errDescription);
				})
		}
	}

	const handleClickUpdateVersion = () => {
		setShowProjectVersion(true);
	}

	const handleCloseProjectVersion = () => {
		setShowProjectVersion(false);
	}

	const formatDescription = (cell) => {
		return <a target='_blank' href={cell} className='has-external-link'>{t('releaseLink')}</a>
	}

	return (
		<div className="fleet-admin-releases">
			<GreyHeader title={t('titleReleases')} hideSubtitle />

			<div className='page-subheader'>
				<div className="subheader-section date-flex">
					<label className="search-label">{t('labelDateTime')}</label>
					<CalendarDropdown
						date={moment(timestamp)}
						displayFormat='MM/DD/YYYY hh:mm A'
						onChange={onDateChange}
						footer={
							<BaseTimePicker
								time={moment(timestamp)}
								timeFormat='hh:mm A'
								onChange={onTimeChange}
							/>
						}
					/>
				</div>
				<div className={`subheader-section search-flex ${errwikilink && 'has-error'}`}>
					<label className="search-label">{t('labelDescription')}</label>
					<input
						type="text"
						name="wikilink"
						className="vehicle-search"
						value={wikilink}
						onChange={onDescriptionChange}
					/>
					{errwikilink && <span className="help-block">{errwikilink}</span>}
				</div>
				<div className='subheader-section'>
					<CustomButton
						size='sm'
						onClick={onAddRelease}
						variant="primary-outline"
					>
						{t('btnAddRelease')}
					</CustomButton>
					<CustomButton
						size='sm'
						onClick={handleClickUpdateVersion}
						variant="primary-outline"
					>
						{t('btnUpdateVersion')}
					</CustomButton>
				</div>
			</div>

			<main className="main-content-block">
				<BootstrapTable
					data={releases}
					{...tableAdjustableColOptions}
				>
					<TableHeaderColumn
						isKey
						width="30%"
						dataField="timestamp"
						dataSort
						dataFormat={formatTimestamp}
					>
						{renderCustomHeader(0, t('headerDateTime'))}
					</TableHeaderColumn>
					<TableHeaderColumn
						width="70%"
						dataField="wikilink"
						dataFormat={formatDescription}
					>
						{t('headerDescription')}
					</TableHeaderColumn>
				</BootstrapTable>
			</main>

			{showProjectVersion
				&& (
					<ModalProjectVersion
						onHide={handleCloseProjectVersion}
					/>
				)}
		</div>
	)
}

export default ReleasesPage;
